<script setup lang="ts">
import { computed } from 'vue';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CheckIcon, ChevronDownIcon } from '@heroicons/vue/24/solid';
import {
  type AbilityFlagKey,
  type AbilityLevel,
  LEVEL_RANK,
  levelFromFlags,
} from '@/components/permissions/abilityFlagKey';

/**
 * Single-select level picker for the 5 (model, ability) flags. Picking a
 * level grants every lower level cumulatively — "Create" means View +
 * Edit + Create, not Create alone. This matches how the underlying
 * policies actually compose: you can't update a row you can't view, and
 * creating a record implies being able to edit it, so isolating one bit
 * without the bits below it is meaningless in practice.
 *
 * Trigger label reads the highest-granted level. Selecting from the
 * menu emits a single `set-level` event; the parent rewrites the row's
 * flags accordingly (and fans out across views that share the same
 * model — see ByViewPanel.applyLevelChangeAcrossViews).
 */

const LEVEL_LABELS: ReadonlyArray<{ level: AbilityLevel; label: string }> = [
  { level: 'none', label: 'None' },
  { level: 'view', label: 'View' },
  { level: 'edit', label: 'Edit' },
  { level: 'create', label: 'Create' },
  { level: 'delete', label: 'Delete' },
] as const;

const props = defineProps<{
  /**
   * The row's current flag state. The component derives the highest-set
   * level from this. Non-cumulative rows normalise on the next pick.
   */
  flags: Partial<Record<AbilityFlagKey, boolean>>;
  disabled?: boolean;
  /**
   * Highest level the acting user may grant for this model (privilege
   * bounding — you can't grant access you don't have). Levels above this
   * render disabled. Defaults to 'delete' (no bound) so callers that don't
   * pass it are unrestricted.
   */
  maxLevel?: AbilityLevel;
  testIdPrefix?: string;
  ariaLabel?: string;
  triggerClass?: string;
}>();

const emit = defineEmits<{
  /** Picked level — parent should set every bit up to and including it. */
  'set-level': [level: AbilityLevel];
}>();

const currentLevel = computed<AbilityLevel>(() => levelFromFlags(props.flags));

const currentLabel = computed(
  () => LEVEL_LABELS.find((entry) => entry.level === currentLevel.value)?.label ?? 'None',
);

/**
 * Cumulative checked state for the menu items. The zero level ("None") is
 * a special case: it's only checked when nothing else is — it doesn't
 * pile up with the others.
 */
function isLevelChecked(level: AbilityLevel): boolean {
  const current = currentLevel.value;
  if (current === 'none') return level === 'none';
  if (level === 'none') return false;
  return LEVEL_RANK[level] <= LEVEL_RANK[current];
}

/**
 * A level is grantable when it's at or below the acting user's max. Levels
 * above the bound are shown but disabled so the menu still communicates the
 * full scale while preventing out-of-envelope picks. Absent maxLevel = no bound.
 */
function isLevelGrantable(level: AbilityLevel): boolean {
  const bound = props.maxLevel ?? 'delete';
  return LEVEL_RANK[level] <= LEVEL_RANK[bound];
}

function onLevelSelect(level: AbilityLevel) {
  if (!isLevelGrantable(level)) return;
  if (level === currentLevel.value) return;
  emit('set-level', level);
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button
        type="button"
        variant="outline"
        size="sm"
        :disabled="disabled"
        :aria-label="ariaLabel ?? 'Ability level'"
        :data-testid="testIdPrefix ? `${testIdPrefix}-trigger` : undefined"
        :class="triggerClass ?? 'w-56 justify-between'"
      >
        <span
          class="truncate text-left text-xs"
          :class="currentLevel === 'none' ? 'text-muted-foreground italic' : ''"
        >{{ currentLabel }}</span>
        <ChevronDownIcon class="size-3.5 shrink-0 opacity-60" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" class="w-44" role="menu">
      <!-- Plain menu items + a self-rendered indicator: lets us light up
           every level at or below the current pick so sighted users
           read the menu as "Delete implies Create + Edit + View."
           Reka's DropdownMenuRadioItem only renders one indicator and
           couldn't do this.

           For assistive tech we go the other way and present each item
           as a single-select radio: `role="menuitemradio"` plus
           `aria-checked` tied to the exact-level match (not the
           cumulative state). The two views are consistent — both
           encode "one level is currently selected" — and the
           cumulative checkmarks stay a sighted-user affordance only.

           A short sr-only "Selected" / level-context phrase backs the
           role/aria-checked pair so screen readers that announce the
           visual label without the state still convey the active level. -->
      <DropdownMenuItem
        v-for="entry in LEVEL_LABELS"
        :key="entry.level"
        inset
        role="menuitemradio"
        :aria-checked="entry.level === currentLevel"
        :disabled="!isLevelGrantable(entry.level)"
        :data-testid="testIdPrefix ? `${testIdPrefix}-${entry.level}` : undefined"
        :data-checked="isLevelChecked(entry.level) ? '' : undefined"
        :data-disabled="!isLevelGrantable(entry.level) ? '' : undefined"
        @select="onLevelSelect(entry.level)"
      >
        <span class="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
          <CheckIcon v-if="isLevelChecked(entry.level)" class="size-3.5" />
        </span>
        {{ entry.label }}
        <span v-if="entry.level === currentLevel" class="sr-only"> (selected)</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
